// src/lib/pwa/session-activity.ts
//
// "Is the student mid-session right now?" — a tiny, dependency-free flag that
// lets the service-worker updater hold its reload until the student is at a
// safe point.
//
// Why this exists: every deploy stamps a new sw.js, the new worker takes
// control, and ServiceWorkerRegistration reloads the tab once so it runs the
// fresh bundle (see that file for the staleness history it fixes). That's
// correct for a tab sitting on the dashboard — but it fires just as happily in
// the middle of a practice question or an SAT module. The 2026-07-22 report
// ("clicked Ask Sage and it jumped to the next question") was exactly this: a
// prod deploy reloaded a live practice tab mid-interaction.
//
// So session surfaces ACQUIRE this flag while work is in flight, and the
// updater defers to it. The flag lives on <html data-ut-session-active> so it's
// inspectable in DevTools (and matches the existing data-sage-docked /
// data-welcome-tour-active conventions); a ref-count keeps two overlapping
// surfaces from clearing each other's claim.

'use client'

import { useEffect } from 'react'

const EVENT = 'ut:session-active-change'

let activeCount = 0

function sync(): void {
  if (typeof document === 'undefined') return
  const el = document.documentElement
  if (activeCount > 0) el.dataset.utSessionActive = '1'
  else delete el.dataset.utSessionActive
  window.dispatchEvent(new CustomEvent(EVENT))
}

/**
 * Claim "a session is in flight". Returns a release fn; releasing twice is a
 * no-op, so a double-invoked effect cleanup can't drive the count negative.
 */
export function acquireSessionActive(): () => void {
  activeCount += 1
  sync()
  let released = false
  return () => {
    if (released) return
    released = true
    activeCount = Math.max(0, activeCount - 1)
    sync()
  }
}

export function isSessionActive(): boolean {
  if (typeof document === 'undefined') return false
  return document.documentElement.dataset.utSessionActive === '1'
}

/** Subscribe to active/idle transitions. Returns an unsubscribe fn. */
export function onSessionActiveChange(cb: () => void): () => void {
  if (typeof window === 'undefined') return () => {}
  window.addEventListener(EVENT, cb)
  return () => window.removeEventListener(EVENT, cb)
}

/**
 * PURE decision for the service-worker updater: reload the tab NOW?
 *
 * - `hadController` false ⇒ first install on a brand-new visitor; never reload.
 * - `refreshing` ⇒ a reload is already underway; never reload twice (loop guard).
 * - `sessionActive` ⇒ the student is mid-question/module; DEFER. The new worker
 *   is already in control, so the next full navigation picks up fresh assets
 *   anyway — and the updater re-checks when the session ends.
 */
export function shouldReloadNow(opts: {
  hadController: boolean
  refreshing: boolean
  sessionActive: boolean
}): boolean {
  if (!opts.hadController) return false
  if (opts.refreshing) return false
  if (opts.sessionActive) return false
  return true
}

/**
 * Hook form: hold the flag while `active` is true, releasing on unmount. Use
 * from a session surface (practice runner, SAT Runner) so a deploy can't reload
 * the tab out from under a student mid-question.
 */
export function useSessionActive(active: boolean): void {
  useEffect(() => {
    if (!active) return
    return acquireSessionActive()
  }, [active])
}

/** Test helper — reset module state between cases. */
export function __resetSessionActivity(): void {
  activeCount = 0
  if (typeof document !== 'undefined') delete document.documentElement.dataset.utSessionActive
}
